// FeatureSection.jsx
import React from 'react';
import { 
  CheckCircle, 
  ArrowRight, 
  Shield, 
  Zap, 
  Users, 
  Cpu,
  Clock,
  Award,
  Star,
  TrendingUp,
  Globe,
  Smartphone,
  Lock,
  Headphones,
  BarChart3,
  Sparkles,
  Factory,
  Microscope,
  Cloud
} from 'lucide-react';
import './InvoterFeatureSection.css';

const FeatureSection = () => {
  const features = [
    {
      id: 1,
      title: "Smart Manufacturing",
      subtitle: "Intelligent Automation",
      description: "Leverage AI-powered insights to optimize your production line. Reduce downtime, increase efficiency, and make data-driven decisions in real-time.",
      image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&h=600&fit=crop&crop=center&auto=format&q=80",
      imageAlt: "Smart manufacturing automation with robotic arms",
      imageLeft: true,
      stats: [
        { value: "99.9%", label: "Uptime" },
        { value: "45%", label: "Efficiency" },
        { value: "2x", label: "Faster" }
      ],
      features: [
        "AI-powered predictive maintenance",
        "Real-time production monitoring",
        "Automated quality control"
      ],
      badge: "Industry 4.0",
      buttonText: "Learn More",
      icon: Factory
    },
    {
      id: 2,
      title: "Digital Twin Technology",
      subtitle: "Virtual Simulation",
      description: "Create virtual replicas of your physical machines. Test scenarios, predict outcomes, and optimize performance without risking actual equipment.",
      image: "https://images.unsplash.com/photo-1581092335871-5f2b5e17cd7a?w=800&h=600&fit=crop&crop=center&auto=format&q=80",
      imageAlt: "Digital twin technology simulation",
      imageLeft: false,
      stats: [
        { value: "92%", label: "Accuracy" },
        { value: "60%", label: "Savings" },
        { value: "3x", label: "Faster" }
      ],
      features: [
        "Real-time virtual simulation",
        "Predictive analytics",
        "Risk-free testing"
      ],
      badge: "Digital Twin",
      buttonText: "Explore",
      icon: Microscope
    },
    {
      id: 3,
      title: "Cloud Connectivity",
      subtitle: "Anywhere, Anytime Access",
      description: "Access your machines and data from anywhere in the world. Secure cloud platform with real-time sync, team collaboration, and mobile-friendly interface.",
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&h=600&fit=crop&crop=center&auto=format&q=80",
      imageAlt: "Cloud connectivity and data center",
      imageLeft: true,
      stats: [
        { value: "24/7", label: "Access" },
        { value: "100%", label: "Secure" },
        { value: "5x", label: "Faster" }
      ],
      features: [
        "Secure cloud storage",
        "Real-time data sync",
        "Multi-device support"
      ],
      badge: "Cloud Native",
      buttonText: "Get Started",
      icon: Cloud
    }
  ];

  return (
    <section className="feature-section">
      <div className="feature-container">
        {/* Section Header - Single Line Premium */}
        <div className="feature-header">
          <div className="feature-header-badge">
            <Sparkles size={16} />
            <span>Premium Features</span>
          </div>
          <h2 className="feature-header-title">
            Everything You Need to <span>Build the Future</span>
          </h2>
          <p className="feature-header-desc">
            Discover our comprehensive suite of tools designed to accelerate your innovation journey.
          </p>
        </div>

        {/* Features */}
        {features.map((feature, index) => {
          const IconComponent = feature.icon;
          return (
            <div 
              key={feature.id} 
              className={`feature-block ${feature.imageLeft ? 'image-left' : 'image-right'}`}
            >
              {/* Image Side */}
              <div className="feature-image-side">
                <div className="feature-image-wrapper">
                  <img
                    src={feature.image}
                    alt={feature.imageAlt}
                    className="feature-image"
                    loading="lazy"
                  />
                  {/* Floating Badge */}
                  <div className="feature-image-badge">
                    <div className="feature-badge-icon">
                      <IconComponent size={20} />
                    </div>
                    <div>
                      <p className="feature-badge-title">{feature.badge}</p>
                      <p className="feature-badge-sub">Active</p>
                    </div>
                  </div>
                  {/* Stats Overlay */}
                  <div className="feature-image-stats">
                    {feature.stats.map((stat, idx) => (
                      <div key={idx} className="feature-stat-item">
                        <p className="feature-stat-value">{stat.value}</p>
                        <p className="feature-stat-label">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Content Side */}
              <div className="feature-content-side">
                <div className="feature-content-wrapper">
                  <div className="feature-content-badge">
                    <IconComponent size={14} />
                    <span>{feature.badge}</span>
                  </div>
                  
                  <h3 className="feature-content-title">
                    {feature.title}
                    <span>{feature.subtitle}</span>
                  </h3>
                  
                  <p className="feature-content-desc">
                    {feature.description}
                  </p>

                  {/* Features List */}
                  <ul className="feature-content-list">
                    {feature.features.map((item, idx) => (
                      <li key={idx} className="feature-list-item">
                        <CheckCircle size={16} className="feature-list-icon" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <button className="feature-content-btn">
                    {feature.buttonText}
                    <ArrowRight size={18} />
                  </button>

                  {/* Trust Badge */}
                  <div className="feature-trust">
                    <div className="feature-trust-avatars">
                      <div className="ft-avatar">A</div>
                      <div className="ft-avatar">B</div>
                      <div className="ft-avatar">C</div>
                      <div className="ft-avatar">D</div>
                      <div className="ft-avatar-more">+2k</div>
                    </div>
                    <div>
                      <p className="feature-trust-text">Trusted by 2,000+ innovators</p>
                      <p className="feature-trust-sub">Premium quality guaranteed</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default FeatureSection;